// Admin Panel -> ⚙ System -> 🔏 Data Privacy -> Erasure Requests.
//
// A request is logged by staff on the patient's behalf (there is no patient
// portal yet — see HMS_V4_PATIENT_APP_DESIGN.md) from the patient's own
// record screen. This is the Admin-only review queue: approve/reject the
// request, then — as a deliberately separate, second action — actually
// complete the redaction. See backend/routes/data-privacy.js's header
// comment for why those are two clicks, not one.
import { useState } from 'react';
import { ShieldOff, KeyRound } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { NativeSelect } from '@/components/ui/Input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/States';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { ApiError } from '@/lib/api/client';
import { useErasureRequests, useReviewErasureRequest, useCompleteErasureRequest, useRevealErasedIdentity } from './hooks';
import type { ErasureRequest, ErasureRequestStatus, RevealedIdentity } from './types';

const dateTime = (v: string) =>
  new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function StatusBadge({ status }: { status: ErasureRequestStatus }) {
  if (status === 'pending') return <Badge variant="warn">Pending review</Badge>;
  if (status === 'approved') return <Badge variant="info">Approved — not yet completed</Badge>;
  if (status === 'rejected') return <Badge variant="neutral">Rejected</Badge>;
  return <Badge variant="danger">Completed (redacted)</Badge>;
}

// Shown inline once revealed, not in an alert() — it's the kind of thing a
// reviewer needs to actually read (and copy, if that's the point of the
// legal-hold request), not glance at and dismiss. Stays open only in this
// component's own state; nothing caches it (see useRevealErasedIdentity's
// own comment on why it's a mutation, not a query).
function RevealedIdentityPanel({ identity, onHide }: { identity: RevealedIdentity; onHide: () => void }) {
  return (
    <div className="mt-1.5 rounded-[var(--radius)] border border-danger/30 bg-danger-soft p-2 text-[11.5px]">
      <div className="mb-1 font-semibold text-danger-ink">Pre-erasure identity — do not screenshot or forward</div>
      <div>Name: {identity.name || '—'}</div>
      <div>Mobile: {identity.mobile || '—'}</div>
      <div>DOB: {identity.dob || '—'}</div>
      <div>Address: {identity.address || '—'}</div>
      <div>ABHA: {identity.abha_number || '—'}</div>
      <Button size="sm" variant="ghost" className="mt-1.5" onClick={onHide}>Hide</Button>
    </div>
  );
}

function RowActions({ row }: { row: ErasureRequest }) {
  const review = useReviewErasureRequest();
  const complete = useCompleteErasureRequest();
  const reveal = useRevealErasedIdentity();
  const [confirmingComplete, setConfirmingComplete] = useState(false);
  const [revealedIdentity, setRevealedIdentity] = useState<RevealedIdentity | null>(null);
  // Reject / reveal ask in a dialog — window.prompt() is refused by embedded browsers.
  const [rejecting, setRejecting] = useState(false);
  const [revealing, setRevealing] = useState(false);

  if (row.status === 'pending') {
    return (
      <div className="flex gap-1.5">
        <Button
          size="sm" variant="ghost"
          disabled={review.isPending}
          onClick={() => review.mutate({ id: row.id, status: 'approved' })}
        >
          Approve
        </Button>
        <Button
          size="sm" variant="ghost"
          disabled={review.isPending}
          onClick={() => { review.reset(); setRejecting(true); }}
        >
          Reject
        </Button>
        <TextPromptDialog
          open={rejecting}
          title="Reject this erasure request?"
          description="The request is closed as rejected and the patient's details stay as they are."
          label="Reason for rejecting (shown in the record)"
          optional
          multiline
          confirmLabel="Reject request"
          danger
          pending={review.isPending}
          error={review.error instanceof Error ? review.error.message : null}
          onConfirm={(notes) => review.mutate(
            { id: row.id, status: 'rejected', resolution_notes: notes || undefined },
            { onSuccess: () => setRejecting(false) },
          )}
          onClose={() => setRejecting(false)}
        />
      </div>
    );
  }

  if (row.status === 'approved') {
    if (confirmingComplete) {
      return (
        <div className="flex flex-col items-end gap-1">
          <div className="text-[11px] text-danger">This permanently redacts name/mobile/DOB/address/ABHA. Continue?</div>
          <div className="flex gap-1.5">
            <Button size="sm" variant="danger" disabled={complete.isPending} onClick={() => complete.mutate(row.id)}>
              {complete.isPending ? 'Redacting…' : 'Confirm redact'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmingComplete(false)}>Cancel</Button>
          </div>
        </div>
      );
    }
    return (
      <Button size="sm" variant="danger" onClick={() => setConfirmingComplete(true)}>
        Complete redaction
      </Button>
    );
  }

  if (row.status === 'completed' && row.has_identity_snapshot) {
    if (revealedIdentity) {
      return <RevealedIdentityPanel identity={revealedIdentity} onHide={() => setRevealedIdentity(null)} />;
    }
    // Gated on the separate admin:legal-hold grant server-side — the
    // person completing an erasure is not automatically the person
    // who can reverse-lookup it later. A reason is mandatory and every
    // call, successful or not, is its own audit_log row.
    return (
      <>
        <Button
          size="sm" variant="ghost"
          disabled={reveal.isPending}
          onClick={() => { reveal.reset(); setRevealing(true); }}
        >
          <KeyRound size={13} /> {reveal.isPending ? 'Revealing…' : 'Reveal identity'}
        </Button>
        <TextPromptDialog
          open={revealing}
          title="Reveal the pre-erasure identity?"
          description="Shows the name and contact details this record held before redaction. Your reason and this access are written to the audit log."
          label="Why do you need this patient's pre-erasure identity?"
          multiline
          confirmLabel="Reveal identity"
          pending={reveal.isPending}
          error={reveal.error ? (reveal.error instanceof ApiError ? reveal.error.message : 'Could not reveal the identity') : null}
          onConfirm={(reason) => reveal.mutate(
            { id: row.id, reason },
            { onSuccess: (data) => { setRevealing(false); setRevealedIdentity(data); } },
          )}
          onClose={() => setRevealing(false)}
        />
      </>
    );
  }

  return <span className="text-[11.5px] text-ink-muted">—</span>;
}

export default function ErasureRequestsTab() {
  const [status, setStatus] = useState<ErasureRequestStatus | ''>('');
  const requests = useErasureRequests(status || undefined);
  const rows = requests.data ?? [];

  return (
    <div className="fade-in">
      <div className="mb-4 text-[13px] leading-relaxed text-ink-muted">
        Requests a patient has asked staff to log on their behalf under the DPDP right to erasure.
        Approving redacts the patient's name, mobile, date of birth, address and ABHA number — the
        UHID and clinical record stay, since Indian medical-record retention rules require the
        record itself to be kept for a statutory period regardless of an erasure request.
      </div>

      <Card>
        <CardHeader>
          <CardTitle><ShieldOff size={15} /> Erasure requests</CardTitle>
          <NativeSelect
            className="h-8 w-auto text-[12px]"
            value={status}
            onChange={(e) => setStatus(e.target.value as ErasureRequestStatus | '')}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending review</option>
            <option value="approved">Approved — not yet completed</option>
            <option value="rejected">Rejected</option>
            <option value="completed">Completed</option>
          </NativeSelect>
        </CardHeader>

        {requests.isError ? (
          <ErrorState
            message={requests.error instanceof ApiError ? requests.error.message : 'Could not load erasure requests'}
            onRetry={() => requests.refetch()}
          />
        ) : requests.isLoading ? (
          <LoadingState label="Loading erasure requests…" />
        ) : rows.length === 0 ? (
          <EmptyState label="No erasure requests" hint="Requests logged from a patient's record screen appear here." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Patient</TableHead>
                <TableHead>Requested by</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Logged</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reviewed by</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="font-semibold">{r.patient_name}</div>
                    <div className="text-[11px] text-ink-muted">{r.uhid}</div>
                  </TableCell>
                  <TableCell>{r.requested_by}</TableCell>
                  <TableCell className="max-w-[220px] truncate" title={r.reason || undefined}>{r.reason || '—'}</TableCell>
                  <TableCell>{dateTime(r.created_at)}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell>
                    {r.reviewed_by ? (
                      <>
                        <div>{r.reviewed_by}</div>
                        {r.reviewed_at && <div className="text-[11px] text-ink-muted">{dateTime(r.reviewed_at)}</div>}
                      </>
                    ) : '—'}
                  </TableCell>
                  <TableCell><RowActions row={r} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
